import React, { useMemo, useState } from 'react';
import {
  ForecastSeriesResponse,
  HolidayKey,
  SeasonAnomalyProduct,
  WeekSeriesPoint,
} from '../../types/AiForecasting';
import {
  YEAR_LINE_COLORS,
  format_english_month_year,
  format_english_week_range,
  format_hebrew_week_range,
  hebcal_labels_for_week,
  hebcal_season_columns,
  hex_to_rgba,
  holiday_core_columns,
  hover_slot_lines,
  peak_indexes,
  SeasonBand,
  SEASON_WASH_ALPHA,
  season_legend_items,
  season_trend_words,
  trim_week_indexes,
  week_anomaly_lines,
  year_change_words,
} from './seasonChartUtils';
import {
  HEBREW_YEAR_WEEKS,
  TISHREI_AXIS_MONTHS,
  hebrew_week_index_for_ymd,
} from './hebrewCalendar';

const WIDTH = 960;
const HEIGHT = 400;
const PAD_LEFT = 72;
const PAD_RIGHT = 20;
const PAD_TOP = 20;
const PAD_BOTTOM = 96;
const CORE_WASH_ALPHA = 0.5;

function x_at(index: number, count: number): number {
  const inner = WIDTH - PAD_LEFT - PAD_RIGHT;
  if (count <= 1) {
    return PAD_LEFT + inner / 2;
  }
  return PAD_LEFT + (index / (count - 1)) * inner;
}

function y_at(value: number, max_y: number, min_y = 0): number {
  const inner = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const span = max_y - min_y;
  const ratio = span <= 0 ? 0 : (value - min_y) / span;
  return PAD_TOP + inner - ratio * inner;
}

function plot_index_from_svg_x(x: number, plot_count: number): number | null {
  if (plot_count <= 1) {
    return plot_count === 1 ? 0 : null;
  }
  const inner = WIDTH - PAD_LEFT - PAD_RIGHT;
  const t = (x - PAD_LEFT) / inner;
  if (t < -0.02 || t > 1.02) {
    return null;
  }
  return Math.max(0, Math.min(plot_count - 1, Math.round(t * (plot_count - 1))));
}

function shift_bands_to_span(
  bands: SeasonBand[],
  span: { start: number; end: number }
): SeasonBand[] {
  return bands
    .filter((band) => band.end_index >= span.start && band.start_index <= span.end)
    .map((band) => ({
      ...band,
      start_index: Math.max(0, band.start_index - span.start),
      end_index: Math.min(Math.max(0, span.end - span.start), band.end_index - span.start),
    }));
}

function band_box(band: SeasonBand, plot_count: number): { x: number; width: number } {
  const left = hover_slot(band.start_index, plot_count);
  const right = hover_slot(band.end_index, plot_count);
  return { x: left.x, width: Math.max(4, right.x + right.width - left.x) };
}

function hover_slot(index: number, count: number): { x: number; width: number } {
  if (count <= 1) {
    return { x: PAD_LEFT, width: WIDTH - PAD_LEFT - PAD_RIGHT };
  }
  const prev = index === 0 ? PAD_LEFT : (x_at(index - 1, count) + x_at(index, count)) / 2;
  const next =
    index === count - 1 ? WIDTH - PAD_RIGHT : (x_at(index, count) + x_at(index + 1, count)) / 2;
  return { x: prev, width: Math.max(4, next - prev) };
}

function format_week_range(week_start: string): string {
  return format_english_week_range(week_start);
}

function format_month_tick(week_start: string): string {
  return format_english_month_year(week_start);
}

function format_cases(n: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n);
}

function label_step(count: number): number {
  if (count <= 12) return 1;
  if (count <= 26) return 2;
  if (count <= 52) return 4;
  return 8;
}

function y_ticks(max_y: number, min_y = 0): number[] {
  if (min_y >= 0) {
    if (max_y <= 1) {
      return [0, 1];
    }
    return [0, Math.round(max_y / 2), max_y];
  }
  return [min_y, 0, max_y];
}

function polyline_from_values(values: number[], max_y: number, min_y = 0): string {
  return values
    .map((value, index) => `${x_at(index, values.length)},${y_at(value, max_y, min_y)}`)
    .join(' ');
}

function HoverSoldBox({ x, lines }: { x: number; lines: string[] }) {
  const box_w = Math.min(520, 24 + Math.max(...lines.map((line) => line.length)) * 6.4);
  const box_h = 10 + lines.length * 16;
  const box_x = Math.min(x + 10, WIDTH - PAD_RIGHT - box_w);
  const box_y = PAD_TOP + 6;
  return (
    <g pointerEvents="none">
      <rect x={box_x} y={box_y} width={box_w} height={box_h} rx={4} fill="#111827" />
      {lines.map((line, index) => (
        <text
          key={`${line}-${index}`}
          x={box_x + 8}
          y={box_y + 16 + index * 16}
          className="season-chart__hover-sold"
        >
          {line}
        </text>
      ))}
    </g>
  );
}

const SERIES_HELP = {
  sales: 'Cases sold that week from posted invoices. Returns and credit memos are subtracted.',
  production:
    'Cases on production orders due that week. Uses actual cases produced when recorded; cancelled orders are left out.',
};

interface SeasonDemandChartProps {
  series: ForecastSeriesResponse;
  weeks: WeekSeriesPoint[];
  selected_years: string[];
  mode: 'all_years' | 'yoy';
  heading?: string;
  subtitle?: string;
  show_legend?: boolean;
  show_seasons?: boolean;
  show_full_year?: boolean;
  highlight_holiday?: HolidayKey;
  holiday_label?: string;
  anomaly_products?: SeasonAnomalyProduct[];
}

const SeasonDemandChart: React.FC<SeasonDemandChartProps> = ({
  series,
  weeks,
  selected_years,
  mode,
  heading,
  subtitle,
  show_legend = false,
  show_seasons = false,
  show_full_year = false,
  highlight_holiday,
  holiday_label,
  anomaly_products,
}) => {
  const [hover, setHover] = useState<number | null>(null);

  if (weeks.length === 0) {
    return (
      <div className="season-chart">
        <div className="season-chart__heading">
          <h2>{heading || (mode === 'yoy' ? 'Year over year' : 'All years')}</h2>
        </div>
        <p className="season-chart__empty">No weekly cases from 2023 onward in this view.</p>
      </div>
    );
  }

  return mode === 'yoy' ? (
    <YoyChart
      series={series}
      weeks={weeks}
      selected_years={selected_years}
      heading={heading}
      subtitle={subtitle}
      show_legend={show_legend}
      show_seasons={show_seasons}
      show_full_year={show_full_year}
      highlight_holiday={highlight_holiday}
      holiday_label={holiday_label}
      anomaly_products={anomaly_products}
      hover={hover}
      setHover={setHover}
    />
  ) : (
    <SingleYearChart
      series={series}
      weeks={weeks}
      hover={hover}
      setHover={setHover}
      heading={heading}
      subtitle={subtitle}
    />
  );
};

const SingleYearChart: React.FC<{
  series: ForecastSeriesResponse;
  weeks: WeekSeriesPoint[];
  hover: number | null;
  setHover: (index: number | null) => void;
  heading?: string;
  subtitle?: string;
}> = ({ series, weeks, hover, setHover, heading, subtitle }) => {
  const max_y = useMemo(() => {
    const peak = Math.max(0, ...weeks.flatMap((w) => [w.sales_cases, w.production_cases]));
    return peak <= 0 ? 1 : Math.ceil(peak * 1.1);
  }, [weeks]);
  const step = label_step(weeks.length);
  const hover_week = hover != null ? weeks[hover] : null;
  const product_title = `${series.product.name} (${series.product.product_code})`;

  return (
    <div className="season-chart">
      <div className="season-chart__heading">
        <h2>{heading || 'All years'}</h2>
        <p className="season-chart__product">{product_title}</p>
        <p className="season-chart__subtitle">
          {subtitle ||
            'Full timeline from the first week with cases (2023 onward). Hover any week for sold cases.'}
        </p>
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`Weekly cases sold versus cases produced for ${product_title}`}
        className="season-chart__svg"
      >
        {y_ticks(max_y).map((tick) => (
          <g key={`ytick-${tick}`}>
            <line
              x1={PAD_LEFT}
              y1={y_at(tick, max_y)}
              x2={WIDTH - PAD_RIGHT}
              y2={y_at(tick, max_y)}
              stroke="#e5e7eb"
            />
            <text
              x={PAD_LEFT - 8}
              y={y_at(tick, max_y) + 4}
              textAnchor="end"
              className="season-chart__axis-label"
            >
              {format_cases(tick)}
            </text>
          </g>
        ))}
        <line
          x1={PAD_LEFT}
          y1={HEIGHT - PAD_BOTTOM}
          x2={WIDTH - PAD_RIGHT}
          y2={HEIGHT - PAD_BOTTOM}
          stroke="#9ca3af"
        />
        <line x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={HEIGHT - PAD_BOTTOM} stroke="#9ca3af" />
        <text
          transform={`translate(16 ${HEIGHT / 2}) rotate(-90)`}
          textAnchor="middle"
          className="season-chart__axis-title"
        >
          Cases per week
        </text>
        <text
          x={(PAD_LEFT + WIDTH - PAD_RIGHT) / 2}
          y={HEIGHT - 12}
          textAnchor="middle"
          className="season-chart__axis-title"
        >
          Week (English date)
        </text>
        <polyline
          fill="none"
          stroke="#1d4ed8"
          strokeWidth="2.5"
          points={polyline_from_values(
            weeks.map((w) => w.sales_cases),
            max_y
          )}
        />
        <polyline
          fill="none"
          stroke="#ea580c"
          strokeWidth="2.5"
          strokeDasharray="6 4"
          points={polyline_from_values(
            weeks.map((w) => w.production_cases),
            max_y
          )}
        />
        {weeks.map((week, index) => {
          const slot = hover_slot(index, weeks.length);
          return (
            <rect
              key={week.week_start}
              x={slot.x}
              y={PAD_TOP}
              width={slot.width}
              height={HEIGHT - PAD_TOP - PAD_BOTTOM}
              fill="transparent"
              onMouseEnter={() => setHover(index)}
              onMouseLeave={() => setHover(null)}
            >
              <title>
                {format_week_range(week.week_start)}: {format_cases(week.sales_cases)} sold cases
              </title>
            </rect>
          );
        })}
        {hover != null && hover_week && (
          <g>
            <line
              x1={x_at(hover, weeks.length)}
              y1={PAD_TOP}
              x2={x_at(hover, weeks.length)}
              y2={HEIGHT - PAD_BOTTOM}
              stroke="#6b7280"
              strokeDasharray="3 3"
            />
            <circle
              cx={x_at(hover, weeks.length)}
              cy={y_at(hover_week.sales_cases, max_y)}
              r={4}
              fill="#1d4ed8"
            />
            <HoverSoldBox
              x={x_at(hover, weeks.length)}
              lines={[
                `${format_week_range(hover_week.week_start)} · ${format_hebrew_week_range(hover_week.week_start)}`,
                `${format_cases(hover_week.sales_cases)} sold cases`,
              ]}
            />
          </g>
        )}
        {weeks.map((week, index) =>
          index % step === 0 || index === weeks.length - 1 ? (
            <text
              key={`label-${week.week_start}`}
              x={x_at(index, weeks.length)}
              y={HEIGHT - 36}
              textAnchor="middle"
              className="season-chart__axis-label"
            >
              {format_month_tick(week.week_start)}
            </text>
          ) : null
        )}
      </svg>
      <div className="season-chart__tooltip" aria-live="polite">
        {hover_week ? (
          <>
            <strong>
              {format_week_range(hover_week.week_start)} · {format_hebrew_week_range(hover_week.week_start)}
            </strong>
            <span title={SERIES_HELP.sales}>Sold {format_cases(hover_week.sales_cases)} cases</span>
          </>
        ) : (
          <span>Hover any week to see how many cases were sold.</span>
        )}
      </div>
      <div className="season-chart__legend">
        <span className="season-chart__legend-item" title={SERIES_HELP.sales}>
          <i className="season-chart__swatch season-chart__swatch--sales" />
          Cases sold (invoices)
        </span>
        <span className="season-chart__legend-item" title={SERIES_HELP.production}>
          <i className="season-chart__swatch season-chart__swatch--production" />
          Cases produced (production orders)
        </span>
      </div>
    </div>
  );
};

const YoyChart: React.FC<{
  series: ForecastSeriesResponse;
  weeks: WeekSeriesPoint[];
  selected_years: string[];
  heading?: string;
  subtitle?: string;
  show_legend: boolean;
  show_seasons: boolean;
  show_full_year: boolean;
  highlight_holiday?: HolidayKey;
  holiday_label?: string;
  anomaly_products?: SeasonAnomalyProduct[];
  hover: number | null;
  setHover: (index: number | null) => void;
}> = ({
  series,
  weeks,
  selected_years,
  heading,
  subtitle,
  show_legend,
  show_seasons,
  show_full_year,
  highlight_holiday,
  holiday_label,
  anomaly_products,
  hover,
  setHover,
}) => {
  const x_count = HEBREW_YEAR_WEEKS;
  const aligned = useMemo(() => {
    return selected_years.map((year) => {
      const sales = Array(x_count).fill(0);
      const production = Array(x_count).fill(0);
      const slot_weeks: Array<WeekSeriesPoint | null> = Array(x_count).fill(null);
      for (const week of weeks) {
        if (week.week_start.slice(0, 4) !== year) {
          continue;
        }
        const n = hebrew_week_index_for_ymd(week.week_start);
        sales[n] += week.sales_cases;
        production[n] += week.production_cases;
        slot_weeks[n] = week;
      }
      return { year, sales, production, slot_weeks };
    });
  }, [selected_years, weeks, x_count]);

  const span = useMemo(() => {
    if (show_full_year) {
      return { start: 0, end: x_count - 1 };
    }
    return trim_week_indexes(
      aligned.flatMap((row) => [row.sales as number[], row.production as number[]])
    );
  }, [aligned, show_full_year, x_count]);

  const plot = useMemo(() => {
    if (span.start > span.end) {
      return [];
    }
    return aligned.map((row) => ({
      year: row.year,
      sales: row.sales.slice(span.start, span.end + 1),
      production: row.production.slice(span.start, span.end + 1),
      slot_weeks: row.slot_weeks.slice(span.start, span.end + 1),
    }));
  }, [aligned, span]);

  const bands = useMemo(() => {
    if (!show_seasons) {
      return [];
    }
    return shift_bands_to_span(
      hebcal_season_columns(
        series.holiday_windows || [],
        highlight_holiday,
        weeks,
        series.product.activity_seasons || []
      ),
      span
    );
  }, [show_seasons, series.holiday_windows, series.product.activity_seasons, highlight_holiday, weeks, span]);

  const core_bands = useMemo(() => {
    if (!show_seasons) {
      return [];
    }
    return shift_bands_to_span(
      holiday_core_columns(series.holiday_windows || [], highlight_holiday),
      span
    );
  }, [show_seasons, series.holiday_windows, highlight_holiday, span]);

  const season_legend = season_legend_items(bands, series.product.activity_seasons, weeks);
  const plot_count = plot[0]?.sales.length || 0;
  const active_hover = hover != null && hover < plot_count ? hover : null;
  const raw_values = plot.flatMap((row) => [...row.sales, ...row.production]);
  const max_y = Math.max(1, ...raw_values.map((n) => Math.ceil(Math.max(0, n) * 1.1)));
  const min_y = Math.min(0, ...raw_values.map((n) => (n < 0 ? Math.floor(n * 1.1) : 0)));
  const product_title = `${series.product.name} (${series.product.product_code})`;
  const is_all_products = series.product.product_id === 0;
  const hover_week_index =
    active_hover != null && span.start <= span.end ? span.start + active_hover : null;
  const hover_week =
    active_hover == null
      ? null
      : plot.map((row) => row.slot_weeks[active_hover]).find((week) => week) || null;
  const hover_season_names = Array.from(
    new Set(
      (active_hover == null
        ? []
        : plot.flatMap((row) =>
            hebcal_labels_for_week(row.slot_weeks[active_hover]?.week_start, series.holiday_windows)
          )
      ).concat(hover_week ? hebcal_labels_for_week(hover_week.week_start, series.holiday_windows) : [])
    )
  );
  const hover_lines =
    active_hover == null
      ? []
      : hover_slot_lines({
          years: plot.map((row) => ({
            year: row.year,
            week_start: row.slot_weeks[active_hover]?.week_start || null,
            sold: row.sales[active_hover] || 0,
            produced: row.production[active_hover] || 0,
          })),
          season_names: hover_season_names,
          scope: is_all_products ? 'all' : 'product',
        });
  const anomaly_by_id = useMemo(() => {
    const map = new Map<number, SeasonAnomalyProduct>();
    for (const row of anomaly_products || []) {
      map.set(row.product_id, row);
    }
    return map;
  }, [anomaly_products]);
  const peaks = useMemo(() => {
    return plot.flatMap((row, year_index) =>
      peak_indexes(row.sales).map((index) => {
        const week = row.slot_weeks[index];
        const from_week = week?.peak_product;
        const peak_product =
          !is_all_products && series.product.product_id
            ? {
                product_id: series.product.product_id,
                product_code: series.product.product_code,
                name: series.product.name,
                cases: row.sales[index] || 0,
              }
            : from_week && from_week.product_id
              ? from_week
              : null;
        const selected_anomaly = anomaly_products?.[0];
        return {
          key: `${row.year}-${index}`,
          year: row.year,
          year_index,
          index,
          sold: row.sales[index] || 0,
          week,
          peak_product,
          anomaly: is_all_products
            ? undefined
            : selected_anomaly ||
              (peak_product ? anomaly_by_id.get(peak_product.product_id) : undefined),
        };
      })
    );
  }, [plot, series.product, anomaly_by_id, anomaly_products, is_all_products]);

  return (
    <div className="season-chart">
      <div className="season-chart__heading">
        <h2>{heading || 'Year over year'}</h2>
        <p className="season-chart__product">{product_title}</p>
        <p className="season-chart__subtitle">
          {subtitle ||
            `Same season week for ${selected_years.join(' vs ') || 'the selected year'}. Hover any week for sold cases.`}
        </p>
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="season-chart__svg"
        role="img"
        aria-label={`Year over year cases for ${product_title}`}
        onMouseMove={(event) => {
          const svg = event.currentTarget;
          const ctm = svg.getScreenCTM();
          if (!ctm) {
            return;
          }
          const pt = svg.createSVGPoint();
          pt.x = event.clientX;
          pt.y = event.clientY;
          const loc = pt.matrixTransform(ctm.inverse());
          setHover(plot_index_from_svg_x(loc.x, plot_count));
        }}
        onMouseLeave={() => setHover(null)}
      >
        {y_ticks(max_y, min_y).map((tick) => (
          <g key={`ytick-${tick}`}>
            <line
              x1={PAD_LEFT}
              y1={y_at(tick, max_y, min_y)}
              x2={WIDTH - PAD_RIGHT}
              y2={y_at(tick, max_y, min_y)}
              stroke="#e5e7eb"
            />
            <text
              x={PAD_LEFT - 8}
              y={y_at(tick, max_y, min_y) + 4}
              textAnchor="end"
              className="season-chart__axis-label"
            >
              {format_cases(tick)}
            </text>
          </g>
        ))}
        {bands.map((band) => {
          const box = band_box(band, plot_count);
          return (
            <g key={`${band.keys.join('+')}-${band.start_index}`} pointerEvents="none">
              <rect
                x={box.x}
                y={PAD_TOP}
                width={box.width}
                height={HEIGHT - PAD_TOP - PAD_BOTTOM}
                fill={hex_to_rgba(band.color, SEASON_WASH_ALPHA)}
              />
              <rect x={box.x} y={PAD_TOP} width={box.width} height={6} fill={band.color} />
            </g>
          );
        })}
        {core_bands.map((band) => {
          const box = band_box(band, plot_count);
          return (
            <rect
              key={`core-${band.keys.join('+')}-${band.start_index}`}
              x={box.x}
              y={PAD_TOP}
              width={box.width}
              height={HEIGHT - PAD_TOP - PAD_BOTTOM}
              fill={hex_to_rgba(band.color, CORE_WASH_ALPHA)}
              pointerEvents="none"
            />
          );
        })}
        <line
          x1={PAD_LEFT}
          y1={HEIGHT - PAD_BOTTOM}
          x2={WIDTH - PAD_RIGHT}
          y2={HEIGHT - PAD_BOTTOM}
          stroke="#9ca3af"
        />
        <line x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={HEIGHT - PAD_BOTTOM} stroke="#9ca3af" />
        <text
          transform={`translate(16 ${HEIGHT / 2}) rotate(-90)`}
          textAnchor="middle"
          className="season-chart__axis-title"
        >
          Cases per week
        </text>
        <text
          x={(PAD_LEFT + WIDTH - PAD_RIGHT) / 2}
          y={HEIGHT - 12}
          textAnchor="middle"
          className="season-chart__axis-title"
        >
          Season calendar (Tishrei → Elul)
        </text>
        {plot.map((row, year_index) => {
          const color = YEAR_LINE_COLORS[year_index % YEAR_LINE_COLORS.length];
          return (
            <g key={row.year} pointerEvents="none">
              <polyline
                fill="none"
                stroke={color}
                strokeWidth="2.5"
                points={polyline_from_values(row.sales, max_y, min_y)}
              />
              <polyline
                fill="none"
                stroke={color}
                strokeWidth="2"
                strokeDasharray="6 4"
                points={polyline_from_values(row.production, max_y, min_y)}
              />
              {row.sales.map((sold, index) => (
                <circle
                  key={`pt-${row.year}-${index}`}
                  cx={x_at(index, plot_count)}
                  cy={y_at(sold, max_y, min_y)}
                  r={2.25}
                  fill={color}
                />
              ))}
            </g>
          );
        })}
        {peaks.map((peak) => {
          const color = YEAR_LINE_COLORS[peak.year_index % YEAR_LINE_COLORS.length];
          const week_label = peak.week
            ? `${format_english_week_range(peak.week.week_start)} · ${format_hebrew_week_range(peak.week.week_start)}`
            : `${peak.year}`;
          const slot_sales = plot.map((row) => ({
            year: row.year,
            sold: row.sales[peak.index] || 0,
          }));
          const title = [
            `Peak ${week_label}`,
            ...week_anomaly_lines(slot_sales, is_all_products ? 'all' : 'product'),
            !is_all_products ? season_trend_words(peak.anomaly, holiday_label || '') : '',
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <g key={peak.key} pointerEvents="none">
              <circle
                cx={x_at(peak.index, plot_count)}
                cy={y_at(peak.sold, max_y, min_y)}
                r={6}
                fill="#fff"
                stroke={color}
                strokeWidth="2.5"
              >
                <title>{title}</title>
              </circle>
            </g>
          );
        })}
        {active_hover != null && hover_week_index != null && plot_count > 0 && (
          <g pointerEvents="none">
            <line
              x1={x_at(active_hover, plot_count)}
              y1={PAD_TOP}
              x2={x_at(active_hover, plot_count)}
              y2={HEIGHT - PAD_BOTTOM}
              stroke="#6b7280"
              strokeDasharray="3 3"
            />
            {plot.map((row, year_index) => (
              <circle
                key={`dot-${row.year}`}
                cx={x_at(active_hover, plot_count)}
                cy={y_at(row.sales[active_hover] || 0, max_y, min_y)}
                r={4.5}
                fill={YEAR_LINE_COLORS[year_index % YEAR_LINE_COLORS.length]}
              />
            ))}
            <HoverSoldBox
              x={x_at(active_hover, plot_count)}
              lines={[
                ...hover_lines,
                ...(!is_all_products && anomaly_products?.[0]
                  ? [
                      season_trend_words(anomaly_products[0], holiday_label || ''),
                      year_change_words(anomaly_products[0]),
                    ].filter(Boolean)
                  : []),
              ]}
            />
          </g>
        )}
        {plot_count > 0 &&
          TISHREI_AXIS_MONTHS.map((name, slot) => {
            const inner = WIDTH - PAD_LEFT - PAD_RIGHT;
            const x = PAD_LEFT + ((slot + 0.5) / TISHREI_AXIS_MONTHS.length) * inner;
            const pill_w = Math.max(56, name.length * 6.8 + 16);
            const pill_h = 20;
            const y = HEIGHT - 48;
            return (
              <g key={name}>
                <rect
                  x={x - pill_w / 2}
                  y={y - 14}
                  width={pill_w}
                  height={pill_h}
                  rx={10}
                  fill="#f3f4f6"
                />
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  className="season-chart__month-label"
                >
                  {name}
                </text>
              </g>
            );
          })}
      </svg>
      <div className="season-chart__tooltip" aria-live="polite">
        {hover_week_index != null ? (
          <>
            {hover_lines.map((line) =>
              line === hover_lines[0] ? (
                <strong key={line}>{line}</strong>
              ) : (
                <span key={line}>{line}</span>
              )
            )}
            {!is_all_products && anomaly_products?.[0] && (
              <>
                <span>{season_trend_words(anomaly_products[0], holiday_label || '')}</span>
                {year_change_words(anomaly_products[0]) && (
                  <span>{year_change_words(anomaly_products[0])}</span>
                )}
              </>
            )}
          </>
        ) : (
          <span>
            Hover anywhere on the chart to see English and Hebrew dates and sold/produced for each
            year.
          </span>
        )}
      </div>
      <div
        className={
          show_legend || show_seasons
            ? 'season-chart__legend season-chart__legend--boxed'
            : 'season-chart__legend'
        }
      >
        {(show_legend || show_seasons) && <p className="season-chart__legend-title">Legend</p>}
        <div className="season-chart__legend-group">
          <span className="season-chart__legend-heading">Calendar years</span>
          {plot.map((row, year_index) => (
            <span key={row.year} className="season-chart__legend-item">
              <i
                className="season-chart__swatch"
                style={{ background: YEAR_LINE_COLORS[year_index % YEAR_LINE_COLORS.length] }}
              />
              {row.year} sold (solid) / produced (dashed)
            </span>
          ))}
          <span className="season-chart__legend-item">
            <i className="season-chart__swatch season-chart__swatch--peak" />
            Peak week dot — hover for the product and anomaly
          </span>
        </div>
        {show_seasons && (
          <div className="season-chart__legend-group">
            <span className="season-chart__legend-heading">Seasons</span>
            {season_legend.length === 0 ? (
              <span className="season-chart__legend-item">No holiday seasons on this view</span>
            ) : (
              season_legend.map((item) => (
                <span key={item.label} className="season-chart__legend-item">
                  <i className="season-chart__swatch" style={{ background: item.color }} />
                  {item.label}
                </span>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SeasonDemandChart;
